import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  projectsApi, testimonialsApi, enquiriesApi,
  contentApi, filtersApi, dashboardApi, ctaApi,
} from '../../services/api';
import { useAdminAuth } from './AdminAuthContext';

const AdminDataContext = createContext(null);

export function AdminDataProvider({ children }) {
  const { admin } = useAdminAuth();
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [ctaEvents, setCtaEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [contentSections, setContentSections] = useState({});
  const [filterTags, setFilterTags] = useState({ categories: [], technologies: [], statuses: [] });
  const [stats, setStats] = useState({
    totalProjects: 0, ongoingProjects: 0, completedProjects: 0, upcomingProjects: 0,
    totalEnquiries: 0, newEnquiries: 0, totalTestimonials: 0, ctaClicks: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Fetch helpers ─────────────────────────────────────────────────

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectsApi.getAll();
      setProjects(res.data || res || []);
    } catch (e) { console.error('projects fetch failed', e); }
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      const data = await testimonialsApi.getAll(true); // admin mode
      setTestimonials(Array.isArray(data) ? data : []);
    } catch (e) { console.error('testimonials fetch failed', e); }
  }, []);

  const fetchEnquiries = useCallback(async () => {
    try {
      const data = await enquiriesApi.getAll();
      setEnquiries(Array.isArray(data) ? data : []);
    } catch (e) { console.error('enquiries fetch failed', e); }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await dashboardApi.getStats();
      if (res.stats) setStats(res.stats);
      if (res.activities) setActivities(res.activities);
    } catch (e) { console.error('dashboard fetch failed', e); }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const data = await contentApi.getAll();
      setContentSections(data || {});
    } catch (e) { console.error('content fetch failed', e); }
  }, []);

  const fetchFilters = useCallback(async () => {
    try {
      const data = await filtersApi.getAll();
      setFilterTags(data || { categories: [], technologies: [], statuses: [] });
    } catch (e) { console.error('filters fetch failed', e); }
  }, []);

  const fetchCta = useCallback(async () => {
    try {
      const data = await ctaApi.getAll();
      setCtaEvents(Array.isArray(data) ? data : []);
    } catch (e) { console.error('cta fetch failed', e); }
  }, []);

  // Initial load - triggers whenever admin status changes (login / logout)
  useEffect(() => {
    if (!admin) {
      setProjects([]);
      setTestimonials([]);
      setEnquiries([]);
      setCtaEvents([]);
      setActivities([]);
      setStats({
        totalProjects: 0, ongoingProjects: 0, completedProjects: 0, upcomingProjects: 0,
        totalEnquiries: 0, newEnquiries: 0, totalTestimonials: 0, ctaClicks: 0,
      });
      return;
    }

    setLoading(true);
    Promise.all([
      fetchProjects(),
      fetchTestimonials(),
      fetchEnquiries(),
      fetchDashboard(),
      fetchContent(),
      fetchFilters(),
      fetchCta(),
    ]).finally(() => setLoading(false));
  }, [admin, fetchProjects, fetchTestimonials, fetchEnquiries, fetchDashboard, fetchContent, fetchFilters, fetchCta]);

  // ── Project CRUD ──────────────────────────────────────────────────

  const addProject = async (project) => {
    const res = await projectsApi.create(project);
    setProjects(prev => [res, ...prev]);
    await fetchDashboard();
    return res;
  };

  const updateProject = async (id, updates) => {
    const res = await projectsApi.update(id, updates);
    setProjects(prev => prev.map(p => p.id === id ? res : p));
    return res;
  };

  const deleteProject = async (id) => {
    await projectsApi.delete(id);
    setProjects(prev => prev.filter(p => p.id !== id));
    await fetchDashboard();
  };

  // ── Testimonials CRUD ─────────────────────────────────────────────

  const addTestimonial = async (testimonial) => {
    const res = await testimonialsApi.create(testimonial);
    setTestimonials(prev => [res, ...prev]);
    await fetchDashboard();
    return res;
  };

  const updateTestimonial = async (id, updates) => {
    const res = await testimonialsApi.update(id, updates);
    setTestimonials(prev => prev.map(t => t.id === id ? res : t));
    return res;
  };

  const deleteTestimonial = async (id) => {
    await testimonialsApi.delete(id);
    setTestimonials(prev => prev.filter(t => t.id !== id));
    await fetchDashboard();
  };

  // ── Enquiry Management ────────────────────────────────────────────

  const updateEnquiryStatus = async (id, status) => {
    const res = await enquiriesApi.updateStatus(id, status);
    setEnquiries(prev => prev.map(e => e.id === id ? { ...e, status } : e));
    await fetchDashboard();
    return res;
  };

  const deleteEnquiry = async (id) => {
    await enquiriesApi.delete(id);
    setEnquiries(prev => prev.filter(e => e.id !== id));
    await fetchDashboard();
  };

  // ── Content Management ────────────────────────────────────────────

  const updateContentSection = async (section, data) => {
    await contentApi.updateSection(section, data);
    setContentSections(prev => ({ ...prev, [section]: { ...prev[section], ...data } }));
  };

  // ── Filter Tags ────────────────────────────────────────────────────

  const addFilterTag = async (category, tag) => {
    await filtersApi.addTag(category, tag);
    setFilterTags(prev => ({ ...prev, [category]: [...(prev[category] || []), tag] }));
  };

  const removeFilterTag = async (category, tag) => {
    await filtersApi.removeTag(category, tag);
    setFilterTags(prev => ({ ...prev, [category]: prev[category].filter(t => t !== tag) }));
  };

  // ── Refresh helpers (for child pages) ─────────────────────────────
  const refreshProjects = fetchProjects;
  const refreshTestimonials = fetchTestimonials;
  const refreshEnquiries = fetchEnquiries;
  const refreshDashboard = fetchDashboard;

  return (
    <AdminDataContext.Provider value={{
      // State
      projects, testimonials, enquiries, ctaEvents, activities,
      contentSections, filterTags, stats, loading, error,

      // Project CRUD
      addProject, updateProject, deleteProject, refreshProjects,

      // Testimonial CRUD
      addTestimonial, updateTestimonial, deleteTestimonial, refreshTestimonials,

      // Enquiry management
      updateEnquiryStatus, deleteEnquiry, refreshEnquiries,

      // Content
      updateContentSection,

      // Filters
      addFilterTag, removeFilterTag,

      // Dashboard
      refreshDashboard,
    }}>
      {children}
    </AdminDataContext.Provider>
  );
}

export function useAdminData() {
  const ctx = useContext(AdminDataContext);
  if (!ctx) throw new Error('useAdminData must be used within AdminDataProvider');
  return ctx;
}
